import React, { useEffect, useState } from 'react'
import ControlBoardComponent from '../board/ControlBoard'
import Dispatcher, { IAction } from '../../flux/Dispatcher'
import EditBoardComponent from '../board/EditBoard'
import {
    addBoard,
    CREATE_BOARD,
    moveBoard,
} from '../../flux/boards/BoardActions'
import { IBoardView } from '../board/Board'
import BoardStore, { EventsEnum } from '../../flux/boards/BoardStore'
import BoardNameComponent from '../board/BoardName'
import MoveBoardComponent from '../board/MoveBoard'
import SettingsStore from '../../flux/settings/SettingsStore'

function ControlBoardsComponent() {
    const settingsStore = SettingsStore.GetInstance()
    const [boardIds, setBoardIds] = useState(BoardStore.getOrderedBoardsIds())
    const [isCreate, setCreate] = useState(false)
    const [move, setMove] = useState({ isMove: false, boardId: -1 })
    const [focus, setFocus] = useState(settingsStore.Focus)

    /**
     * Invoked to save a new board to the store
     * @param board Board to save / add
     */
    function handleSave(board: IBoardView) {
        addBoard(board)
        setCreate(false)
    }

    /**
     * Invoked when cancelling a CREATE_BOARD action.
     * Removed the edit board component
     */
    function handleCancel() {
        setCreate(false)
    }

    /**
     * Invoked when move is clicked on a board component.
     * Sets the control into "Move Mode"
     * @param boardId ID of the board to focus
     */
    function handleMove(boardId: number) {
        setMove({ isMove: true, boardId })
    }

    /**
     * Monitors dispatcher for create board action.
     * Changes state when found to show component
     * @param action Action
     */
    function handleActions(action: IAction) {
        if (action.actionType === CREATE_BOARD) {
            setCreate(true)
        }
    }

    /**
     * Updates the boards from the store on either:
     * Board Store State Change,
     * Add or Delete
     */
    function updateBoards() {
        setBoardIds(BoardStore.getOrderedBoardsIds())
    }

    /**
     * Handles the up event originating from BoardMoveComponent.
     * Triggers move board event to move up in list
     */
    function handleUp() {
        const index = boardIds.findIndex(
            (_boardId) => move.boardId === _boardId
        )
        moveBoard(move.boardId, boardIds[index - 1])
    }

    /**
     * Handles the down event originating from BoardMoveComponent.
     * Triggers move board event to move down in list
     */
    function handleDown() {
        const index = boardIds.findIndex(
            (_boardId) => move.boardId === _boardId
        )
        moveBoard(move.boardId, boardIds[index + 1])
    }

    /**
     * Handles the done event from BoardMoveComponent.
     * Exits move mode
     */
    function handleDone() {
        setMove({ isMove: false, boardId: -1 })
    }

    useEffect(() => {
        /**
         * Invoked when the change event triggers on the settings store.
         * Updates the components focus state from the store.
         */
        function updateFocus() {
            setFocus(settingsStore.Focus)
        }

        const handle = Dispatcher.register(handleActions)
        BoardStore.addBoardStoreListener(EventsEnum.ADD, updateBoards)
        BoardStore.addBoardStoreListener(EventsEnum.DELETE, updateBoards)
        BoardStore.addBoardStoreListener(EventsEnum.MOVE, updateBoards)
        BoardStore.addStateChangeListener(updateBoards)
        settingsStore.addChangeListener(updateFocus)
        return () => {
            Dispatcher.unregister(handle)
            BoardStore.removeBoardStoreListener(EventsEnum.ADD, updateBoards)
            BoardStore.removeBoardStoreListener(EventsEnum.DELETE, updateBoards)
            BoardStore.removeBoardStoreListener(EventsEnum.MOVE, updateBoards)
            BoardStore.removeStateChangeListener(updateBoards)
            settingsStore.removeChangeListener(updateFocus)
        }
    }, [boardIds, settingsStore])

    return (
        <>
            {isCreate && (
                <EditBoardComponent
                    board={{ id: -1, name: '', order: -1, swimlaneIds: [] }}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            )}
            {!move.isMove &&
                boardIds.map((boardId) => (
                    <ControlBoardComponent
                        key={boardId}
                        boardId={boardId}
                        canMove={boardIds.length !== 1}
                        canFocus={boardIds.length !== 1}
                        isHidden={focus.isFocused && focus.boardId !== boardId}
                        isFocused={focus.isFocused && focus.boardId === boardId}
                        onMove={handleMove}
                    />
                ))}
            {move.isMove && (
                <div className="container">
                    {boardIds.map((boardId, index) => (
                        <div key={boardId} className="card mt-3">
                            <div
                                className={`card-body ${
                                    move.boardId === boardId
                                        ? 'bg-secondary rounded-top'
                                        : ''
                                }`}>
                                <BoardNameComponent boardId={boardId} />
                            </div>
                            {move.boardId === boardId && (
                                <div className="card-footer">
                                    <MoveBoardComponent
                                        showUp={index !== 0}
                                        showDown={index !== boardIds.length - 1}
                                        onUp={handleUp}
                                        onDone={handleDone}
                                        onDown={handleDown}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}

export default ControlBoardsComponent
